import React, { useMemo } from 'react';
import { Alert, Button, Card, Col, Row, Space, Typography, Image } from 'antd';
import { FileOutlined, EditOutlined, InboxOutlined } from '@ant-design/icons';
import { Price } from '@partsbadger/library';
import { ContactProps, OrderLineItemProps, SalesOrderProps } from '@/types/types';
import { QuoteProps } from '@partsbadger/types';
const { Text } = Typography;

type OrderPartViewModel = {
    orderItemId: number;
    id: string;
    name: string;

    thumbnailUrl?: string;

    quoteFile3DName?: string;
    quoteFile2DName?: string;
    quoteHasFile3D?: boolean;
    quoteHasFile2D?: boolean;

    production2DFileName?: string;
    production3DFileName?: string;

    production2DFileId?: number;
    production3DFileId?: number;
    quoteFile3DUrl?: string; // attachments.file3d.file_obj
    quoteFile2DUrl?: string; // attachments.files_2d.file

    selections: {
        process?: string;
        material?: string;
        surfaceFinish?: string;
        coating?: string;
        quantity: number;
    };

    totalPrice: number;
    unitPrice?: number | null;
    leadTimeDays?: number | null;
};

const displayNames: Record<string, string> = {
    process: 'Manufacturing Process',
    material: 'Material',
    surfaceFinish: 'Surface Finish',
    coating: 'Coating',
};

const addBusinessDays = (start: Date, businessDays: number) => {
    const d = new Date(start);
    let added = 0;

    while (added < businessDays) {
        d.setDate(d.getDate() + 1);
        const day = d.getDay(); // 0 Sun, 6 Sat
        if (day !== 0 && day !== 6) added += 1;
    }

    return d;
};

const formatDateShort = (date: Date) =>
    new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    }).format(date);

const getShipDateDisplay = (days?: number | null) => {
    if (days == null) {
        return { dateLabel: 'TBD', subLabel: '' };
    }

    const today = new Date();
    const shipDate = addBusinessDays(today, days);

    return {
        dateLabel: formatDateShort(shipDate),
        subLabel: `(${days} business ${days === 1 ? 'day' : 'days'})`,
    };
};

function orderItemToVM(
  item: OrderLineItemProps,
  leadTimeByQuoteLineItemId?: Map<number, number>,
  quoteLookups?: {
    byLineItemId: Map<number, any>;
    byMasterProductId: Map<number, any>;
    byName: Map<string, any>;
  }
): OrderPartViewModel {
    const qhp: any = (item as any).quote_has_product;
    const orderMasterProductId =
        typeof (item as any)?.master_product === 'number' ? (item as any).master_product : null;

    const orderName = String((item as any)?.display_name ?? item.name ?? qhp?.name ?? '').trim();

    const fullQuoteLineItem =
        (typeof qhp?.id === 'number' ? quoteLookups?.byLineItemId.get(qhp.id) : null) ??
        (typeof orderMasterProductId === 'number'
            ? quoteLookups?.byMasterProductId.get(orderMasterProductId)
            : null) ??
        (orderName ? quoteLookups?.byName.get(orderName) : null) ??
        null;
    const quote3d = fullQuoteLineItem?.product?.attachments?.file3d;
    const quote2d = fullQuoteLineItem?.product?.attachments?.files_2d;

    const material = qhp?.material_related?.name ?? (item as any)?.material_related?.name ?? '';
    const finish = qhp?.finish_related?.name ?? (item as any)?.finish_related?.name ?? '';
    const coating = qhp?.coating_related?.name ?? (item as any)?.coating_related?.name ?? '';
    const process =
        qhp?.manufacturing_type ?? (item as any)?.manufacturing_type ?? (item as any)?.process ?? 'CNC Machining';

    const quoteFile3DName =
        qhp?.attachments?.file3d?.filename ?? qhp?.product?.attachments?.file3d?.filename ?? quote3d?.filename ?? '';

    const quoteFile2DName =
        qhp?.attachments?.files_2d?.filename ??
        qhp?.product?.attachments?.files_2d?.filename ??
        quote2d?.filename ??
        '';

    const quoteFile3DUrl =
        qhp?.attachments?.file3d?.file_obj ?? qhp?.product?.attachments?.file3d?.file_obj ?? quote3d?.file_obj ?? '';

    const quoteFile2DUrl =
        qhp?.attachments?.files_2d?.file ?? qhp?.product?.attachments?.files_2d?.file ?? quote2d?.file ?? '';

    const quoteHasFile3D = Boolean(
        qhp?.has_file_3d ??
        qhp?.attachments?.file3d ??
        qhp?.product?.attachments?.file3d ??
        quote3d
    );

    const quoteHasFile2D = Boolean(
        qhp?.has_file_2d ??
        qhp?.attachments?.files_2d ??
        qhp?.product?.attachments?.files_2d ??
        quote2d
    );

    // Order item production files
    const production2DFileName =
        (item as any)?.production_2d_file?.filename ?? (item as any)?.production_2d_file?.original_filename ?? '';
    const production3DFileName =
        (item as any)?.production_3d_file?.filename ?? (item as any)?.production_3d_file?.original_filename ?? '';

    const production3DFileId =
        typeof (item as any)?.production_3d_file?.id === 'number' ? (item as any).production_3d_file.id : undefined;

    const production2DFileId =
        typeof (item as any)?.production_2d_file?.id === 'number' ? (item as any).production_2d_file.id : undefined;

    const total =
        typeof (item as any)?.total_price === 'number'
            ? (item as any).total_price
            : (item as any)?.total_price
            ? parseFloat((item as any).total_price)
            : 0;

    const leadTimeDays =
        typeof (item as any)?.lead_time === 'number'
            ? (item as any).lead_time
            : (item as any)?.lead_time
            ? parseInt((item as any).lead_time, 10)
            : typeof qhp?.lead_time === 'number'
            ? qhp.lead_time
            : qhp?.lead_time
            ? parseInt(qhp.lead_time, 10)
            : typeof fullQuoteLineItem?.lead_time === 'number'
            ? fullQuoteLineItem.lead_time
            : fullQuoteLineItem?.lead_time
            ? parseInt(fullQuoteLineItem.lead_time, 10)
            : null;

    const unit =
        typeof (item as any)?.unit_price === 'number'
            ? (item as any).unit_price
            : (item as any)?.unit_price
            ? parseFloat((item as any).unit_price)
            : typeof qhp?.unit_price === 'number'
            ? qhp.unit_price
            : qhp?.unit_price
            ? parseFloat(qhp.unit_price)
            : null;

    return {
        orderItemId: item.id,
        id: String(item.id),
        name: (item as any).display_name ?? item.name ?? qhp?.name ?? 'Part',
        thumbnailUrl: qhp?.thumbnail ?? undefined,

        quoteFile3DName,
        quoteFile2DName,
        quoteHasFile3D,
        quoteHasFile2D,

        production2DFileName,
        production3DFileName,
        quoteFile2DUrl,
        quoteFile3DUrl,
        production3DFileId,
        production2DFileId,
        selections: {
            process,
            material,
            surfaceFinish: finish,
            coating,
            quantity: item.quantity ?? 1,
        },

        totalPrice: total,
        unitPrice: unit,
        leadTimeDays,
    };
}

interface OrderItemsReviewProps {
    order: SalesOrderProps;
    order_items: OrderLineItemProps[];
    contact: ContactProps;
    quote?: QuoteProps;

    onEditOrder?: () => void;
    mode?: 'checkout' | 'reorder';
}

const OrderItemsReview: React.FC<OrderItemsReviewProps> = ({ order, order_items, onEditOrder, quote, mode = 'checkout', }) => {
    const isReorder = mode === 'reorder';

    const quoteLineItemById = useMemo(() => {
        const map = new Map<number, any>();
        const lis: any[] = (quote as any)?.line_items ?? [];
        for (const li of lis) {
            if (typeof li?.id === 'number') map.set(li.id, li);
        }
        return map;
    }, [quote]);

    const quoteLookups = useMemo(() => {
        const byLineItemId = new Map<number, any>();
        const byMasterProductId = new Map<number, any>();
        const byName = new Map<string, any>();

        const lis: any[] = (quote as any)?.line_items ?? [];

        for (const li of lis) {
            if (typeof li?.id === 'number') {
            byLineItemId.set(li.id, li);
            }

            const masterProductId = li?.product?.master_product ?? li?.master_product_details?.id;
            if (typeof masterProductId === 'number' && !byMasterProductId.has(masterProductId)) {
            byMasterProductId.set(masterProductId, li);
            }

            const name = String(li?.name ?? li?.product?.name ?? '').trim();
            if (name && !byName.has(name)) {
            byName.set(name, li);
            }
        }

        return { byLineItemId, byMasterProductId, byName };
    }, [quote]);

    const leadTimeByQuoteLineItemId = useMemo(() => {
        const map = new Map<number, number>();
        const lineItems: any[] = (quote as any)?.line_items ?? [];

        for (const li of lineItems) {
            const id = li?.id;
            const lt = li?.lead_time;
            if (typeof id === 'number' && typeof lt === 'number') {
                map.set(id, lt);
            }
        }

        return map;
    }, [quote]);

    const isRealPartLineItem = (li: any) => {
        if (li?.quote_has_product) return true;
        if (li?.master_product) return true;

        return false;
    };

    const parts = useMemo(
        () =>
            (order_items ?? [])
            .filter(isRealPartLineItem)
            .map(item => orderItemToVM(item, leadTimeByQuoteLineItemId, quoteLookups)),
        [order_items, leadTimeByQuoteLineItemId, quoteLookups]
    );

    const isDraft = order?.status === 'Draft';

    return (
        <Card
            title={
                <Space align="center">
                    <InboxOutlined />
                    <span>Parts Review</span>
                </Space>
            }
            extra={
                !isReorder && onEditOrder ? ( // hide in reorder
                <Button style={{ marginTop: 15 }} icon={<EditOutlined />} size="small" onClick={onEditOrder}>
                    Edit Order
                </Button>
                ) : null
            }
            style={{ marginBottom: 24 }}
        >
            <Text type="secondary">Review your parts and confirm production file status before checkout.</Text>

            <div style={{ marginTop: 16 }}>
                <Space direction="vertical" style={{ width: '100%' }} size={16}>
                    {parts.map((part, index) => {
                        const hasProd2D = Boolean(part.production2DFileName);
                        const hasProd3D = Boolean(part.production3DFileName);

                        // “Pending” logic:
                        // If quote has files but production missing, still treat as pending in old checkout logic
                        const pending2D = !hasProd2D;
                        const pending3D = !hasProd3D;

                        return (
                            <Card
                                key={part.id}
                                size="small"
                                style={{ borderRadius: 8 }}
                                title={
                                    <Space align="center">
                                        <Space align="center">
                                            <div
                                                className="rounded-lg overflow-hidden border border-slate-200 bg-slate-100"
                                                style={{
                                                    width: 40,
                                                    height: 40,
                                                }}
                                            >
                                                {part.thumbnailUrl ? (
                                                    <Image
                                                        alt={part.quoteFile3DName || part.quoteFile2DName || part.name}
                                                        src={part.thumbnailUrl}
                                                        preview={false}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                        }}
                                                    />
                                                ) : (
                                                    <div
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: 16,
                                                            color: '#999',
                                                        }}
                                                    >
                                                        <InboxOutlined />
                                                    </div>
                                                )}
                                            </div>

                                            <div>
                                                <div style={{ fontWeight: 600 }}>{part.name}</div>

                                                {/* Show quote file 3D if exists */}
                                                {part.quoteHasFile3D && part.quoteFile3DName ? (
                                                    <div
                                                        className="flex items-center gap-2 text-xs"
                                                        style={{ color: '#64748b', marginTop: 2 }}
                                                    >
                                                        <FileOutlined style={{ fontSize: 11 }} />
                                                        <span className="truncate">{part.quoteFile3DName}</span>
                                                    </div>
                                                ) : null}

                                                {/* fallback: quote 2D name */}
                                                {!part.quoteHasFile3D && part.quoteHasFile2D && part.quoteFile2DName ? (
                                                    <div
                                                        className="flex items-center gap-2 text-xs"
                                                        style={{ color: '#64748b', marginTop: 2 }}
                                                    >
                                                        <FileOutlined style={{ fontSize: 11 }} />
                                                        <span className="truncate">{part.quoteFile2DName}</span>
                                                    </div>
                                                ) : null}
                                            </div>
                                        </Space>
                                    </Space>
                                }
                            >
                                {/* Specs */}
                                <Row gutter={[16, 8]} style={{ marginBottom: 16 }}>
                                    {Object.entries(part.selections).map(([key, value]) => {
                                        if (key === 'quantity') return null;
                                        return (
                                            <Col xs={24} md={12} key={key} style={{ fontSize: 12 }}>
                                                <Text type="secondary">{displayNames[key] || key}:</Text>{' '}
                                                <Text strong>{String(value) || '—'}</Text>
                                            </Col>
                                        );
                                    })}
                                </Row>

                                {/* Quantity / Price */}
                                <Card
                                    size="small"
                                    style={{
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: 8,
                                        marginBottom: 16,
                                    }}
                                >
                                    <Row gutter={16}>
                                        <Col xs={24} sm={8}>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                Quantity
                                            </Text>
                                            <div style={{ fontWeight: 600 }}>{part.selections.quantity}</div>
                                        </Col>

                                        <Col xs={24} sm={8}>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                Est. Ship Date
                                            </Text>
                                            {(() => {
                                                const ship = getShipDateDisplay(part.leadTimeDays);
                                                return (
                                                    <div>
                                                        <div style={{ fontWeight: 600 }}>{ship.dateLabel}</div>
                                                        {ship.subLabel ? (
                                                            <div
                                                                style={{
                                                                    fontSize: 12,
                                                                    color: '#64748b',
                                                                    fontWeight: 400,
                                                                }}
                                                            >
                                                                {ship.subLabel}
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                );
                                            })()}
                                        </Col>

                                        <Col xs={24} sm={8}>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                Total Cost
                                            </Text>
                                            <div>
                                                <div style={{ fontWeight: 600, color: '#1677ff' }}>
                                                    {part.totalPrice > 0 ? <Price value={part.totalPrice} /> : '—'}
                                                </div>

                                                {part.unitPrice != null && part.unitPrice > 0 ? (
                                                    <div style={{ fontSize: 12, color: '#64748b', fontWeight: 400 }}>
                                                        <Price value={part.unitPrice} /> ea.
                                                    </div>
                                                ) : null}
                                            </div>
                                        </Col>
                                    </Row>
                                </Card>

                                {/* Production files status */}
                                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                                    <Space style={{ width: '100%' }} align="center">
                                        <Text strong>Production Files</Text>
                                    </Space>

                                    {/* 2D */}
                                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            2D
                                        </Text>
                                        {hasProd2D ? (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const url = part.quoteFile2DUrl;
                                                    if (url) window.open(url, '_blank', 'noreferrer');
                                                }}
                                                disabled={!part.quoteFile2DUrl}
                                                className="flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1 hover:bg-green-100 disabled:opacity-60 disabled:cursor-not-allowed"
                                                title={part.production2DFileId ? 'Open / download' : 'TBD'}
                                            >
                                                <FileOutlined className="text-green-600" />
                                                <span className="truncate max-w-[220px]">
                                                    {part.production2DFileName}
                                                </span>
                                            </button>
                                        ) : (
                                            <Text type="warning" style={{ fontSize: 12 }}>
                                                Pending 2D file
                                            </Text>
                                        )}
                                    </Space>

                                    {/* 3D */}
                                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            3D
                                        </Text>
                                        {hasProd3D ? (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const url = part.quoteFile3DUrl;
                                                    if (url) window.open(url, '_blank', 'noreferrer');
                                                }}
                                                disabled={!part.quoteFile3DUrl}
                                                className="flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1 hover:bg-green-100 disabled:opacity-60 disabled:cursor-not-allowed"
                                                title={part.production3DFileId ? 'Open / download' : 'TBD'}
                                            >
                                                <FileOutlined className="text-green-600" />
                                                <span className="truncate max-w-[220px]">
                                                    {part.production3DFileName}
                                                </span>
                                            </button>
                                        ) : (
                                            <Text type="warning" style={{ fontSize: 12 }}>
                                                Pending 3D file
                                            </Text>
                                        )}
                                    </Space>

                                    {(pending2D || pending3D) && (
                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                            If files are pending, complete them in the order editor before checkout.
                                        </Text>
                                    )}
                                </Space>
                            </Card>
                        );
                    })}
                {!isReorder && (
                    <>
                    <Alert
                        type="warning"
                        showIcon
                        message={
                            <span>
                                <strong>Note:</strong> Parts and production files should be finalized before placing the
                                order.
                                {onEditOrder ? ' Click “Edit Order” to update items/files.' : ''}
                            </span>
                        }
                    />

                    {!isDraft && (
                        <Alert
                            type="info"
                            showIcon
                            message="This order is not a Draft. You may be redirected if checkout is not allowed."
                        />
                    )}
                    </>
                   )}
                </Space>
            </div>
        </Card>
    );
};

export default OrderItemsReview;
